<script setup>
// Botão flutuante "?" — refaz o tour da tela atual (e o de visão geral).
// Só aparece em telas que TÊM tour. Também dispara o tour automaticamente na
// primeira visita do usuário (com um respiro pra tela terminar de montar).
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTour, destroyActiveTour } from 'dashboard/composables/useTour';

const route = useRoute();
const { t } = useI18n();
const {
  startTour,
  maybeAutoStart,
  tourForRoute,
  toursDisabled,
  setToursDisabled,
  resetTours,
} = useTour();

const menuOpen = ref(false);
const currentTour = computed(() => tourForRoute(route.name));

let autoStartTimer = null;
watch(
  () => route.name,
  routeName => {
    menuOpen.value = false;
    clearTimeout(autoStartTimer);
    // Navegou (ex.: clicou no item destacado de um passo interativo):
    // derruba o tour da tela antiga; o da nova assume após montar.
    destroyActiveTour();
    // Espera a tela montar (skeletons/fetches) antes de destacar elementos.
    autoStartTimer = setTimeout(() => maybeAutoStart(routeName), 1800);
  },
  { immediate: true }
);

onBeforeUnmount(() => clearTimeout(autoStartTimer));

const replayCurrent = () => {
  menuOpen.value = false;
  startTour(currentTour.value);
};

const replayOverview = () => {
  menuOpen.value = false;
  startTour('overview');
};

const toggleAutoTours = () => {
  setToursDisabled(!toursDisabled());
  menuOpen.value = false;
};

const restartOnboarding = () => {
  resetTours();
  menuOpen.value = false;
  startTour('overview');
};
</script>

<template>
  <div class="fixed bottom-5 z-40 ltr:right-5 rtl:left-5">
    <div
      v-if="menuOpen"
      class="mb-2 flex flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-lg"
    >
      <button
        v-if="currentTour"
        type="button"
        class="px-4 py-2.5 text-left text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-1"
        @click="replayCurrent"
      >
        {{ t('TOUR.MENU.REPLAY_SCREEN') }}
      </button>
      <button
        type="button"
        class="px-4 py-2.5 text-left text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-1"
        @click="replayOverview"
      >
        {{ t('TOUR.MENU.REPLAY_OVERVIEW') }}
      </button>
      <button
        type="button"
        class="px-4 py-2.5 text-left text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-1"
        @click="restartOnboarding"
      >
        {{ t('TOUR.MENU.RESTART_ALL') }}
      </button>
      <button
        type="button"
        class="border-t border-n-weak px-4 py-2.5 text-left text-sm text-n-slate-11 transition-colors hover:bg-n-alpha-1"
        @click="toggleAutoTours"
      >
        {{
          toursDisabled()
            ? t('TOUR.MENU.ENABLE_AUTO')
            : t('TOUR.MENU.DISABLE_AUTO')
        }}
      </button>
    </div>
    <button
      v-tooltip.left="t('TOUR.MENU.TOOLTIP')"
      type="button"
      class="ml-auto flex size-10 items-center justify-center rounded-full border border-n-weak bg-n-solid-2 text-n-slate-11 shadow-lg transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
      @click="menuOpen = !menuOpen"
    >
      <span class="i-lucide-circle-help text-xl" />
    </button>
  </div>
</template>
